import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './OverflowMenu.css';

/* Menú de tres puntos (F2, ESTANDAR §7: acciones frecuentes visibles, el resto
   acá). Renderiza vía Portal con posición fija calculada del trigger — funciona
   dentro de tablas con scroll sin recortarse. Cierra con click afuera, Escape,
   scroll o resize. Ítems destructivos en rojo (la confirmación la pone el caller). */
export default function OverflowMenu({ items = [], ariaLabel = 'Más acciones', className = '' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = () => {
    const r = btnRef.current.getBoundingClientRect();
    const menuH = Math.min(items.length * 38 + 10, 300);
    const abre = r.bottom + menuH > window.innerHeight - 8;
    setPos({
      top: abre ? r.top - menuH - 4 : r.bottom + 4,
      right: Math.max(8, window.innerWidth - r.right),
    });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === 'mousedown' && (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target))) return;
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    window.addEventListener('scroll', () => setOpen(false), { capture: true, once: true });
    window.addEventListener('resize', () => setOpen(false), { once: true });
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  if (!items.length) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`ui-overflow-btn${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : openMenu(); }}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} className="ui-overflow-menu" role="menu" style={{ top: pos.top, right: pos.right }}>
          {items.map((it) => (
            <button
              key={it.id || it.label}
              type="button"
              role="menuitem"
              className={`ui-overflow-item${it.destructive ? ' ui-overflow-item--destructive' : ''}`}
              disabled={it.disabled}
              title={it.title || undefined}
              onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick?.(); }}
            >
              {it.icon && <span className="ui-overflow-icon" aria-hidden="true">{it.icon}</span>}
              {it.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
